import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import {
  detectPlatform,
  signInWithAppleJS,
  signInWithOAuthProtocol,
} from '@/lib/apple-auth';
import { Loader2 } from 'lucide-react';

interface AppleSignInButtonProps {
  className?: string;
  theme?: 'black' | 'white' | 'white-outline';
  type?: 'sign-in' | 'continue' | 'sign-up';
}

const APPLE_TYPE_MAP = {
  'sign-in': 'sign in',
  'continue': 'continue',
  'sign-up': 'sign up',
} as const;

export function AppleSignInButton({
  className = '',
  theme = 'black',
  type = 'sign-in',
}: AppleSignInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Render Apple's official button
  useEffect(() => {
    const el = containerRef.current;
    if (!el || isLoading) return;

    // Clear previous render
    const btnDiv = el.querySelector('#appleid-signin');
    if (btnDiv) btnDiv.innerHTML = '';

    // Wait for Apple JS SDK
    const render = () => {
      if ((window as any).AppleID?.auth) {
        try {
          // Apple's renderButton API renders the official HIG-compliant button
          (window as any).AppleID.auth.renderButton?.();
        } catch {
          // Fallback: SDK auto-renders based on data attributes
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(render, 100);
    return () => clearTimeout(timer);
  }, [theme, type, isLoading]);

  const handleAppleSignIn = async () => {
    setIsLoading(true);

    try {
      const platform = detectPlatform();

      if (platform === 'android') {
        signInWithOAuthProtocol('buxbox');
        return;
      }

      const { idToken, code, user } = await signInWithAppleJS();

      navigate('/auth-loading', {
        state: { idToken, code, user, platform },
        replace: true,
      });
    } catch (error: any) {
      console.error('[AppleSignIn] Error:', error);
      const message = error?.message || 'Could not sign in with Apple. Please try again.';
      if (message !== 'Sign in cancelled') {
        toast({
          title: 'Sign in failed',
          description: message,
          variant: 'destructive',
        });
      }
      setIsLoading(false);
    }
  };

  const logoColor = theme === 'black' ? '#FFFFFF' : '#000000';

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {isLoading ? (
        /* Loading state overlay - matches Apple button dimensions */
        <button
          disabled
          className="inline-flex items-center justify-center gap-2 w-full disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            backgroundColor: theme === 'black' ? '#000000' : '#FFFFFF',
            color: theme === 'black' ? '#FFFFFF' : '#000000',
            border: theme === 'white-outline' ? '1px solid #000000' : 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize: '17px',
            fontWeight: 500,
            letterSpacing: '-0.22px',
            lineHeight: '1.18',
            height: '48px',
            borderRadius: '14px',
            padding: '0 16px',
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label="Signing in with Apple"
        >
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: logoColor }} />
          <span>Signing in…</span>
        </button>
      ) : (
        /* Apple's official button - click intercepted for our auth flow */
        <div
          className="relative w-full"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAppleSignIn();
          }}
          style={{ cursor: 'pointer' }}
        >
          <div
            id="appleid-signin"
            data-mode="center-align"
            data-type="continue"
            data-color={theme === 'white-outline' ? 'white' : theme}
            data-border="false"
            data-border-radius="14"
            data-width="100%"
            data-height="48"
            style={{ pointerEvents: 'none' }}
          />
        </div>
      )}
    </div>
  );
}
